import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useBrand, brandLogo } from "@/lib/brand";

export function BrandLogo({ className = "h-8 w-auto", light = false }) {
  const b = useBrand();
  if (!b) return <span data-testid="brand-logo-loading" className={`inline-block ${className}`} aria-hidden="true" />;
  if (!b.primary && !b.logo_url) {
    return <span data-testid="brand-logo-text" title={b.name} className={`inline-block max-w-[9rem] sm:max-w-[12rem] truncate whitespace-nowrap font-display font-extrabold uppercase tracking-wide leading-tight !h-auto text-sm sm:text-base ${light ? "text-stone-900" : "text-white"}`}>{b.name || "Company"}</span>;
  }
  return <img src={brandLogo(b, light ? "/brand/logo-light.png" : "/brand/logo-dark.png")} alt={b.name} data-testid="brand-logo" className={className} draggable={false} />;
}

// Shared dark branded header: optional back button, website logo, separate page title, page actions on the right.
export function AppHeader({
  title, eyebrow, onBack, backTestId = "back-button", backLabel = "Back", titleTestId = "page-title",
  home = "/", width = "max-w-7xl", children, below,
}) {
  const b = useBrand();
  return (
    <header data-testid="app-header" className="sticky top-0 z-40 shadow-sm">
      <div className="on-dark border-b border-white/10">
      <div className={`${width} mx-auto min-h-[56px] md:min-h-[68px] px-3 sm:px-6 lg:px-8 py-2 flex items-center gap-2 sm:gap-3 md:gap-4`}>
        {onBack && (
          <button type="button" data-testid={backTestId} onClick={onBack} aria-label={backLabel} title={backLabel}
            className="h-10 w-10 shrink-0 -ml-1 inline-flex items-center justify-center rounded-full text-white hover:bg-white/10 transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        <Link to={home} data-testid="brand-home-link" className="shrink-0 transition-opacity hover:opacity-80" aria-label={`${b?.name || ""} home`.trim()}>
          <BrandLogo className="h-6 sm:h-7 md:h-9 w-auto" />
        </Link>
        <span aria-hidden className="h-6 md:h-8 w-px bg-white/20 shrink-0" />
        <div className="min-w-0 flex-1">
          {eyebrow && <div data-testid="page-eyebrow" className="text-[10px] uppercase tracking-[0.2em] font-semibold text-white/75 truncate">{eyebrow}</div>}
          <h1 data-testid={titleTestId} className="font-display text-[14px] sm:text-base md:text-xl font-bold uppercase tracking-[0.05em] text-white line-clamp-2 sm:line-clamp-1 leading-tight">{title}</h1>
        </div>
        {children}
      </div>
      </div>
      {below}
    </header>
  );
}

// Shared pill chip for filters and segmented controls.
export function Chip({ active, onClick, count, tone, testId, children, className = "", ...rest }) {
  const on = tone === "test" ? "bg-fuchsia-700 border-fuchsia-700 text-white" : "bg-stone-700 border-stone-700 text-white";
  return (
    <button type="button" data-testid={testId} onClick={onClick} aria-selected={active} {...rest}
      className={`shrink-0 inline-flex items-center gap-1.5 h-9 lg:h-8 px-3.5 rounded-full border text-[13px] lg:text-xs font-semibold transition-colors ${active ? on : "bg-white border-stone-300 text-stone-700 hover:border-stone-500 hover:text-stone-900"} ${className}`}>
      {children}
      {count !== undefined && <span className={`tabular-nums text-[11px] ${active ? "text-white/75" : "text-stone-500"}`}>{count}</span>}
    </button>
  );
}

export function Eyebrow({ children, className = "" }) {
  return <div className={`text-[10px] uppercase tracking-[0.24em] font-semibold text-stone-500 ${className}`}>{children}</div>;
}
